import { all, put, select, takeLatest } from "redux-saga/effects";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { MapEntity } from "@mapmycustomers/shared/types/entity";
import { ColorPinLegend, ShapePinLegend } from "@mapmycustomers/shared/types/map/PinLegend";
import Organization from "@mapmycustomers/shared/types/Organization";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";

import extractRouteStopEntityIds from "@app/scene/routes/utils/extractRouteStopEntityIds";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import { getAllColorLegends, getAllShapeLegends } from "@app/store/pinLegends";
import type AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import type ListResponseAggregation from "@app/types/viewModel/ListResponseAggregation";
import buildPinLegends from "@app/util/map/buildPinLegends";
import { StopVisualsMap } from "@app/util/map/markerStyles/types/StopVisual";

import {
  applyRouteViewSettings,
  fetchRouteStopVisuals,
  initializeRouteCreate,
  initializeRouteEdit,
  initializeRouteView,
} from "../../actions";
import {
  addPinsToRoute,
  buildRouteFromEditor,
  fetchScheduledActivities,
  insertStop,
  removePinsFromRoute,
  removeScheduledActivity,
} from "../../editor/actions";
import { getRouteEditorStopEntityIds } from "../../editor/selectors";
import { getRoute, getRouteViewState } from "../../selectors";

type StopIds = { companyIds: number[]; personIds: number[] };

export function* selectCurrentStopIds() {
  const route: ReturnType<typeof getRoute> = yield select(getRoute);
  if (route?.stopTime?.length) {
    return extractRouteStopEntityIds(route.stopTime);
  }
  const ids: ReturnType<typeof getRouteEditorStopEntityIds> = yield select(
    getRouteEditorStopEntityIds
  );
  return { companyIds: [...ids.companyIds], personIds: [...ids.personIds] } as StopIds;
}

export function* onFetchRouteStopVisuals({
  payload: { companyIds, personIds },
}: ReturnType<typeof fetchRouteStopVisuals.request>) {
  if (companyIds.length === 0 && personIds.length === 0) {
    yield put(fetchRouteStopVisuals.success({ stopVisuals: {} }));
    return;
  }
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const colorPinLegends: ColorPinLegend[] = yield select(getAllColorLegends);
    const shapePinLegends: ShapePinLegend[] = yield select(getAllShapeLegends);
    const viewState: MapViewState = yield select(getRouteViewState);

    const types: (EntityType.COMPANY | EntityType.PERSON)[] = [];
    const entities: Record<string, unknown> = {};
    if (companyIds.length) {
      types.push(EntityType.COMPANY);
      entities[EntityType.COMPANY] = { $and: [{ id: { $in: companyIds } }] };
    }
    if (personIds.length) {
      types.push(EntityType.PERSON);
      entities[EntityType.PERSON] = { $and: [{ id: { $in: personIds } }] };
    }

    const limit = companyIds.length + personIds.length;
    const pinLegends = buildPinLegends(types, viewState, colorPinLegends, shapePinLegends);
    const visualsRequest = {
      $filters: {
        entities,
        includeCustomFields: true, // not used by web, but needed by platform to calculate coloring correctly
        pinLegends,
      },
      $limit: limit,
    };

    // Colors and shapes only. Deliberately no second request for map-pin envelopes of the same
    // stops: /pins returns envelopes only when the request carries `precision`, and precision
    // makes it group every record sharing a geo cell into one entry that carries no ids at all
    // (verified in mmc-server: the bucket is a geotile_grid cell, and only count === 1 buckets
    // get a `data` field). Two stops within a cell were therefore unresolvable, and no precision
    // value fixes it, since records at identical coordinates share a cell at every precision.
    // So stop clicks identify their record from the stop itself instead of looking up a pin.
    const response: AggregatedListResponse<MapEntity, ListResponseAggregation<MapEntity>[]> =
      yield callApi("fetchPins", orgId, visualsRequest);

    const stopVisuals: StopVisualsMap = {};
    response.data.forEach((entity) => {
      if (entity.entity === EntityType.COMPANY || entity.entity === EntityType.PERSON) {
        stopVisuals[`${entity.entity}:${entity.id}`] = { color: entity.color, shape: entity.shape };
      }
    });

    yield put(fetchRouteStopVisuals.success({ stopVisuals }));
  } catch (error) {
    yield put(fetchRouteStopVisuals.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onRouteStopsResolved() {
  const ids: StopIds = yield* selectCurrentStopIds();
  yield put(fetchRouteStopVisuals.request(ids));
}

export function* onColorShapeKeyChanged({ payload }: ReturnType<typeof applyRouteViewSettings>) {
  if (!("colorKey" in payload) && !("shapeKey" in payload)) {
    return;
  }
  const ids: StopIds = yield* selectCurrentStopIds();
  yield put(fetchRouteStopVisuals.request(ids));
}

export function* stopVisualsSaga() {
  yield all([
    takeLatest(fetchRouteStopVisuals.request, onFetchRouteStopVisuals),
    takeLatest(
      [
        initializeRouteEdit.success,
        initializeRouteView.success,
        initializeRouteCreate.success,
        buildRouteFromEditor.success,
        addPinsToRoute,
        removePinsFromRoute,
        insertStop.success,
        fetchScheduledActivities.success,
        removeScheduledActivity,
      ],
      onRouteStopsResolved
    ),
    takeLatest(applyRouteViewSettings, onColorShapeKeyChanged),
  ]);
}
